import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Plus, 
  Truck, 
  CheckCircle, 
  XCircle, 
  Clock,
  Package,
  ClipboardCheck,
  Search,
  Trash2,
  FileText,
  ShoppingBag,
  Warehouse,
  Eye,
  Edit
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

// Permission lists
const ORDER_USERS = ['arbe', 'admin', 'pabs', 'nicole', 'alma', 'jelyn', 'kristine', 'carla', 'cristel', 'geraldine'];
const APPROVE_USERS = ['arbe', 'admin', 'pabs', 'nicole', 'alma', 'jelyn', 'kristine', 'carla'];
const ARRIVE_USERS = ['arbe', 'admin', 'pabs', 'nicole', 'alma', 'jelyn', 'kristine', 'carla', 'cristel', 'geraldine'];

const SunjinShopOrders = ({ embedded = false }) => {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Product search state
  const [productSearch, setProductSearch] = useState('');
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  
  // Dialogs
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showReceiveDialog, setShowReceiveDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [editingOrder, setEditingOrder] = useState(null);
  
  // Forms
  const [orderForm, setOrderForm] = useState({
    orderDate: new Date().toISOString().split('T')[0],
    expectedDelivery: '',
    supplier: 'Sunjin Philippines',
    items: [],
    paymentTerms: 'cod',
    notes: ''
  });
  
  const [newItem, setNewItem] = useState({
    productId: '',
    productName: '',
    quantity: '',
    unit: 'sack',
    unitPrice: ''
  });
  
  const [receiveForm, setReceiveForm] = useState({
    receivedItems: [],
    notes: ''
  });
  
  const [rejectReason, setRejectReason] = useState('');

  // Access check
  const username = user?.username?.toLowerCase() || '';
  const fullName = user?.full_name?.toLowerCase() || '';
  const isAdmin = user?.role === 'admin' || username === 'arbe';
  
  const canCreateOrders = isAdmin || ORDER_USERS.some(n => username.includes(n) || fullName.includes(n));
  const canApproveOrders = isAdmin || APPROVE_USERS.some(n => username.includes(n) || fullName.includes(n));
  const canReceiveOrders = isAdmin || ARRIVE_USERS.some(n => username.includes(n) || fullName.includes(n));

  useEffect(() => {
    loadOrders();
    loadProducts();
  }, []);

  const loadOrders = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/nas/sunjin/orders`);
      setOrders(res.data || []);
    } catch (error) {
      console.error('Error loading Sunjin orders:', error);
      // Initialize with empty array if endpoint doesn't exist yet
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const loadProducts = async () => {
    try {
      const res = await axios.get(`${API}/api/nas/products`);
      setProducts(res.data || []);
    } catch (error) {
      console.error('Error loading products:', error);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-PH', { style: 'currency', currency: 'PHP' }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-PH', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Add item to order
  const addItemToOrder = () => {
    if (!newItem.productName || !newItem.quantity) {
      toast.error('Please fill in product and quantity');
      return;
    }
    
    setOrderForm({
      ...orderForm,
      items: [...orderForm.items, {
        ...newItem,
        quantity: parseFloat(newItem.quantity),
        unitPrice: parseFloat(newItem.unitPrice) || 0,
        totalPrice: parseFloat(newItem.quantity) * (parseFloat(newItem.unitPrice) || 0)
      }]
    });
    
    setNewItem({
      productId: '',
      productName: '',
      quantity: '',
      unit: 'sack',
      unitPrice: ''
    });
  };

  // Remove item from order
  const removeItemFromOrder = (index) => {
    setOrderForm({
      ...orderForm,
      items: orderForm.items.filter((_, i) => i !== index)
    });
  };

  // Create order
  const handleCreateOrder = async () => {
    if (orderForm.items.length === 0) {
      toast.error('Please add at least one item');
      return;
    }
    
    try {
      const payload = {
        ...orderForm,
        createdBy: user?.full_name || user?.username,
        totalAmount: orderForm.items.reduce((sum, item) => sum + item.totalPrice, 0)
      };
      
      await axios.post(`${API}/api/nas/sunjin/orders`, payload);
      toast.success('Order created successfully!');
      setShowCreateDialog(false);
      resetOrderForm();
      loadOrders();
    } catch (error) {
      console.error('Error creating order:', error);
      toast.error(error.response?.data?.detail || 'Failed to create order');
    }
  };

  // Approve order
  const handleApproveOrder = async (orderId) => {
    try {
      await axios.put(`${API}/api/nas/sunjin/orders/${orderId}/approve`, {
        approvedBy: user?.full_name || user?.username
      });
      toast.success('Order approved!');
      loadOrders();
    } catch (error) {
      toast.error('Failed to approve order');
    }
  };

  // Mark as arrived - adds to Bodega inventory
  const handleMarkArrived = async () => {
    if (!selectedOrder) return;
    
    try {
      await axios.put(`${API}/api/nas/sunjin/orders/${selectedOrder.id}/arrived`, {
        receivedBy: user?.full_name || user?.username,
        receivedItems: receiveForm.receivedItems.length > 0 ? receiveForm.receivedItems : selectedOrder.items,
        notes: receiveForm.notes
      });
      toast.success('Order marked as arrived! Inventory added to Bodega.');
      setShowReceiveDialog(false);
      setSelectedOrder(null);
      setReceiveForm({ receivedItems: [], notes: '' });
      loadOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark as arrived');
    }
  };

  // Reject order
  const handleRejectOrder = async () => {
    if (!selectedOrder || !rejectReason) {
      toast.error('Please provide a reason');
      return;
    }
    
    try {
      await axios.put(`${API}/api/nas/sunjin/orders/${selectedOrder.id}/reject`, {
        rejectedBy: user?.full_name || user?.username,
        reason: rejectReason
      });
      toast.success('Order rejected');
      setShowRejectDialog(false);
      setSelectedOrder(null);
      setRejectReason('');
      loadOrders();
    } catch (error) {
      toast.error('Failed to reject order');
    }
  };

  const resetOrderForm = () => {
    setOrderForm({
      orderDate: new Date().toISOString().split('T')[0],
      expectedDelivery: '',
      supplier: 'Sunjin Philippines',
      items: [],
      paymentTerms: 'cod',
      notes: ''
    });
    setNewItem({
      productId: '',
      productName: '',
      quantity: '',
      unit: 'sack',
      unitPrice: ''
    });
  };

  const openReceiveDialog = (order) => {
    setSelectedOrder(order);
    setReceiveForm({
      receivedItems: order.items.map(item => ({
        ...item,
        receivedQuantity: item.quantity
      })),
      notes: ''
    });
    setShowReceiveDialog(true);
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { color: 'bg-yellow-100 text-yellow-700', icon: Clock },
      approved: { color: 'bg-blue-100 text-blue-700', icon: CheckCircle },
      arrived: { color: 'bg-green-100 text-green-700', icon: Package },
      rejected: { color: 'bg-red-100 text-red-700', icon: XCircle }
    };
    const { color, icon: Icon } = config[status] || config.pending;
    return (
      <Badge className={`${color} flex items-center gap-1`}>
        <Icon size={12} />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  // Filter orders
  const filteredOrders = orders.filter(order => {
    const matchesSearch = !searchTerm || 
      order.orderNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.supplier?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.items?.some(item => item.productName?.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesTab = activeTab === 'all' || order.status === activeTab;
    
    return matchesSearch && matchesTab;
  });

  const orderStats = {
    all: orders.length,
    pending: orders.filter(o => o.status === 'pending').length,
    approved: orders.filter(o => o.status === 'approved').length,
    arrived: orders.filter(o => o.status === 'arrived').length
  };

  return (
    <div className={embedded ? '' : 'p-4 lg:p-6'}>
      {!embedded && (
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <ShoppingBag className="text-orange-600" />
            Sunjin Shop Orders
          </h1>
          <p className="text-slate-500">Order products from Sunjin Shop → Delivered to Bodega</p>
        </div>
      )}

      {/* Header Actions */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
          <Input
            placeholder="Search by supplier, order number, or product..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        {canCreateOrders && (
          <Button onClick={() => setShowCreateDialog(true)} className="bg-orange-600 hover:bg-orange-700">
            <Plus size={18} className="mr-2" />
            New Sunjin Order
          </Button>
        )}
      </div>

      {/* Info Banner */}
      <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6">
        <div className="flex items-center gap-3">
          <Warehouse className="text-orange-600" size={24} />
          <div>
            <p className="font-semibold text-orange-800">Sunjin Shop → Bodega</p>
            <p className="text-sm text-orange-600">When orders are marked as "Arrived", inventory will be added to Bodega stock.</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
        <TabsList>
          <TabsTrigger value="all">All ({orderStats.all})</TabsTrigger>
          <TabsTrigger value="pending">Pending ({orderStats.pending})</TabsTrigger>
          <TabsTrigger value="approved">Approved ({orderStats.approved})</TabsTrigger>
          <TabsTrigger value="arrived">Arrived ({orderStats.arrived})</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Orders List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin h-8 w-8 border-4 border-orange-600 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-slate-500">Loading orders...</p>
        </div>
      ) : filteredOrders.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <ShoppingBag size={48} className="mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500">No orders found</p>
            {canCreateOrders && (
              <Button onClick={() => setShowCreateDialog(true)} className="mt-4 bg-orange-600 hover:bg-orange-700">
                <Plus size={18} className="mr-2" />
                Create First Order
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredOrders.map((order) => (
            <Card key={order.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-bold text-lg">{order.orderNumber || `SJN-${order.id?.slice(0, 8)}`}</h3>
                      {getStatusBadge(order.status)}
                    </div>
                    <div className="text-sm text-slate-500 space-y-1">
                      {order.supplier && (
                        <p className="font-medium text-slate-700">Supplier: {order.supplier}</p>
                      )}
                      <p>Created: {formatDate(order.createdAt)} by {order.createdBy}</p>
                      {order.expectedDelivery && <p>Expected: {formatDate(order.expectedDelivery)}</p>}
                    </div>
                    <div className="mt-2">
                      <p className="text-xs text-slate-400 mb-1">Items:</p>
                      <div className="flex flex-wrap gap-1">
                        {order.items?.slice(0, 3).map((item, idx) => (
                          <Badge key={idx} variant="outline" className="text-xs">
                            {item.productName} x{item.quantity}
                          </Badge>
                        ))}
                        {order.items?.length > 3 && (
                          <Badge variant="outline" className="text-xs">+{order.items.length - 3} more</Badge>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="text-right">
                    <p className="text-2xl font-bold text-orange-600">{formatCurrency(order.totalAmount)}</p>
                    <div className="flex gap-2 mt-2 justify-end flex-wrap">
                      {/* View Details Button */}
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => {
                          setSelectedOrder(order);
                          setShowViewDialog(true);
                        }}
                      >
                        <Eye size={14} className="mr-1" />
                        View
                      </Button>
                      
                      {/* Edit Button - for pending and approved orders */}
                      {(order.status === 'pending' || order.status === 'approved') && canCreateOrders && (
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => {
                            setEditingOrder(order);
                            setOrderForm({
                              orderDate: order.orderDate || new Date().toISOString().split('T')[0],
                              expectedDelivery: order.expectedDelivery || '',
                              supplier: order.supplier || 'Sunjin Philippines',
                              items: order.items || [],
                              paymentTerms: order.paymentTerms || 'cod',
                              notes: order.notes || ''
                            });
                            setShowEditDialog(true);
                          }}
                        >
                          <Edit size={14} className="mr-1" />
                          Edit
                        </Button>
                      )}
                      
                      {order.status === 'pending' && canApproveOrders && (
                        <>
                          <Button 
                            size="sm" 
                            onClick={() => handleApproveOrder(order.id)}
                            className="bg-blue-600 hover:bg-blue-700"
                          >
                            <CheckCircle size={14} className="mr-1" />
                            Approve
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="text-red-600 hover:bg-red-50"
                            onClick={() => {
                              setSelectedOrder(order);
                              setShowRejectDialog(true);
                            }}
                          >
                            <XCircle size={14} className="mr-1" />
                            Reject
                          </Button>
                        </>
                      )}
                      {order.status === 'approved' && canReceiveOrders && (
                        <Button 
                          size="sm" 
                          onClick={() => openReceiveDialog(order)}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          <Package size={14} className="mr-1" />
                          Mark Arrived
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Order Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShoppingBag className="text-orange-600" />
              Create Sunjin Shop Order
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-orange-50 p-3 rounded-lg text-sm text-orange-700">
              <strong>Note:</strong> Items from this order will be added to Bodega inventory when marked as arrived.
            </div>
            
            <div>
              <Label>Supplier Name</Label>
              <Input
                value={orderForm.supplier}
                onChange={(e) => setOrderForm({...orderForm, supplier: e.target.value})}
                placeholder="e.g., Sunjin Philippines, ABC Feeds"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Order Date</Label>
                <Input
                  type="date"
                  value={orderForm.orderDate}
                  onChange={(e) => setOrderForm({...orderForm, orderDate: e.target.value})}
                />
              </div>
              <div>
                <Label>Expected Delivery</Label>
                <Input
                  type="date"
                  value={orderForm.expectedDelivery}
                  onChange={(e) => setOrderForm({...orderForm, expectedDelivery: e.target.value})}
                />
              </div>
            </div>

            <div>
              <Label>Payment Terms</Label>
              <Select value={orderForm.paymentTerms} onValueChange={(v) => setOrderForm({...orderForm, paymentTerms: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cod">Cash on Delivery</SelectItem>
                  <SelectItem value="credit">Credit (30 days)</SelectItem>
                  <SelectItem value="prepaid">Prepaid</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Add Item Section */}
            <div className="border rounded-lg p-4">
              <h4 className="font-semibold mb-3">Add Item</h4>
              <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
                <div className="col-span-2 relative">
                  <Input
                    placeholder="Search product name..."
                    value={productSearch}
                    onChange={(e) => {
                      setProductSearch(e.target.value);
                      setShowProductDropdown(true);
                    }}
                    onFocus={() => setShowProductDropdown(true)}
                  />
                  {showProductDropdown && (
                    <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {products
                        .filter(p => 
                          !productSearch || 
                          p.name?.toLowerCase().includes(productSearch.toLowerCase()) ||
                          p.brand?.toLowerCase().includes(productSearch.toLowerCase()) ||
                          p.supplier?.toLowerCase().includes(productSearch.toLowerCase())
                        )
                        .map(p => {
                          // Get the best available price
                          const price = p.purchasePrice || p.purchasePricePerSack || p.wholesalePrice || p.price || 0;
                          return (
                            <div
                              key={p.id}
                              className="px-3 py-2 hover:bg-orange-50 cursor-pointer border-b last:border-b-0"
                              onClick={() => {
                                setNewItem({
                                  ...newItem,
                                  productId: p.id,
                                  productName: p.name,
                                  unitPrice: price || ''
                                });
                                setProductSearch(p.name);
                                setShowProductDropdown(false);
                              }}
                            >
                              <div className="flex justify-between items-center">
                                <div className="font-medium text-sm">{p.name}</div>
                                {price > 0 && (
                                  <div className="text-sm font-semibold text-orange-600">₱{price.toLocaleString()}</div>
                                )}
                              </div>
                              {(p.brand || p.supplier) && (
                                <div className="text-xs text-slate-500">
                                  {p.brand && <span>{p.brand}</span>}
                                  {p.brand && p.supplier && <span> • </span>}
                                  {p.supplier && <span>{p.supplier}</span>}
                                </div>
                              )}
                            </div>
                          );
                        })
                      }
                      {products.filter(p => 
                        !productSearch || 
                        p.name?.toLowerCase().includes(productSearch.toLowerCase())
                      ).length === 0 && (
                        <div className="px-3 py-4 text-center text-slate-500 text-sm">
                          No products found
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <Input
                  type="number"
                  placeholder="Qty"
                  value={newItem.quantity}
                  onChange={(e) => setNewItem({...newItem, quantity: e.target.value})}
                  onFocus={() => setShowProductDropdown(false)}
                />
                <Select value={newItem.unit} onValueChange={(v) => setNewItem({...newItem, unit: v})}>
                  <SelectTrigger onClick={() => setShowProductDropdown(false)}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sack">Sack</SelectItem>
                    <SelectItem value="kg">KG</SelectItem>
                    <SelectItem value="pcs">Pcs</SelectItem>
                    <SelectItem value="box">Box</SelectItem>
                    <SelectItem value="case">Case</SelectItem>
                  </SelectContent>
                </Select>
                <Input
                  type="number"
                  placeholder="Price"
                  value={newItem.unitPrice}
                  onChange={(e) => setNewItem({...newItem, unitPrice: e.target.value})}
                  onFocus={() => setShowProductDropdown(false)}
                />
              </div>
              <Button onClick={() => { addItemToOrder(); setProductSearch(''); }} className="mt-2 bg-orange-600 hover:bg-orange-700" size="sm">
                <Plus size={14} className="mr-1" /> Add
              </Button>
            </div>

            {/* Order Items */}
            {orderForm.items.length > 0 && (
              <div className="border rounded-lg p-4">
                <h4 className="font-semibold mb-3">Order Items ({orderForm.items.length})</h4>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {orderForm.items.map((item, idx) => (
                    <div key={idx} className="flex justify-between items-center p-2 bg-slate-50 rounded">
                      <div>
                        <span className="font-medium">{item.productName}</span>
                        <span className="text-slate-500 ml-2">{item.quantity} {item.unit} @ {formatCurrency(item.unitPrice)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold">{formatCurrency(item.totalPrice)}</span>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => removeItemFromOrder(idx)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-3 pt-3 border-t flex justify-between font-bold">
                  <span>Total</span>
                  <span className="text-orange-600">
                    {formatCurrency(orderForm.items.reduce((sum, item) => sum + item.totalPrice, 0))}
                  </span>
                </div>
              </div>
            )}

            <div>
              <Label>Notes</Label>
              <Textarea
                value={orderForm.notes}
                onChange={(e) => setOrderForm({...orderForm, notes: e.target.value})}
                placeholder="Additional notes..."
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateOrder} className="bg-orange-600 hover:bg-orange-700">
              Create Order
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mark Arrived Dialog */}
      <Dialog open={showReceiveDialog} onOpenChange={setShowReceiveDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="text-green-600" />
              Mark Order as Arrived
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-green-50 p-3 rounded-lg text-sm text-green-700">
              <strong>Note:</strong> This will add the items to Bodega inventory.
            </div>
            
            {selectedOrder && (
              <div className="border rounded-lg p-4">
                <h4 className="font-semibold mb-3">Items to Receive</h4>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {selectedOrder.items?.map((item, idx) => (
                    <div key={idx} className="flex justify-between items-center p-2 bg-slate-50 rounded">
                      <span>{item.productName}</span>
                      <span className="font-medium">{item.quantity} {item.unit}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            <div>
              <Label>Notes</Label>
              <Textarea
                value={receiveForm.notes}
                onChange={(e) => setReceiveForm({...receiveForm, notes: e.target.value})}
                placeholder="Any notes about the delivery..."
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReceiveDialog(false)}>Cancel</Button>
            <Button onClick={handleMarkArrived} className="bg-green-600 hover:bg-green-700">
              <Package size={16} className="mr-2" />
              Confirm Arrived
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Order</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Reason for Rejection</Label>
              <Textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Please provide a reason..."
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRejectDialog(false)}>Cancel</Button>
            <Button onClick={handleRejectOrder} variant="destructive">
              Reject Order
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Order Details Dialog */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="text-orange-600" />
              Order Details
            </DialogTitle>
          </DialogHeader>
          
          {selectedOrder && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-slate-500">Order Number</p>
                  <p className="font-semibold">{selectedOrder.orderNumber || `SJN-${selectedOrder.id?.slice(0, 8)}`}</p>
                </div>
                <div>
                  <p className="text-slate-500">Status</p>
                  {getStatusBadge(selectedOrder.status)}
                </div>
                <div>
                  <p className="text-slate-500">Supplier</p>
                  <p className="font-semibold">{selectedOrder.supplier || 'Sunjin Philippines'}</p>
                </div>
                <div>
                  <p className="text-slate-500">Order Date</p>
                  <p className="font-semibold">{formatDate(selectedOrder.orderDate || selectedOrder.createdAt)}</p>
                </div>
                <div>
                  <p className="text-slate-500">Expected Delivery</p>
                  <p className="font-semibold">{formatDate(selectedOrder.expectedDelivery) || '-'}</p>
                </div>
                <div>
                  <p className="text-slate-500">Created By</p>
                  <p className="font-semibold">{selectedOrder.createdBy}</p>
                </div>
              </div>
              
              <div className="border rounded-lg p-4">
                <h4 className="font-semibold mb-3">Order Items ({selectedOrder.items?.length || 0})</h4>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {selectedOrder.items?.map((item, idx) => (
                    <div key={idx} className="flex justify-between items-center p-2 bg-slate-50 rounded">
                      <div>
                        <span className="font-medium">{item.productName}</span>
                        <span className="text-slate-500 ml-2">{item.quantity} {item.unit}</span>
                      </div>
                      <span className="font-semibold text-orange-600">{formatCurrency(item.totalPrice || (item.quantity * item.unitPrice))}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-3 pt-3 border-t flex justify-between font-bold text-lg">
                  <span>Total</span>
                  <span className="text-orange-600">{formatCurrency(selectedOrder.totalAmount)}</span>
                </div>
              </div>
              
              {selectedOrder.notes && (
                <div>
                  <p className="text-slate-500 text-sm">Notes</p>
                  <p className="text-sm">{selectedOrder.notes}</p>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowViewDialog(false)}>Close</Button>
            {(selectedOrder?.status === 'pending' || selectedOrder?.status === 'approved') && canCreateOrders && (
              <Button 
                onClick={() => {
                  setShowViewDialog(false);
                  setEditingOrder(selectedOrder);
                  setOrderForm({
                    orderDate: selectedOrder.orderDate || new Date().toISOString().split('T')[0],
                    expectedDelivery: selectedOrder.expectedDelivery || '',
                    supplier: selectedOrder.supplier || 'Sunjin Philippines',
                    items: selectedOrder.items || [],
                    paymentTerms: selectedOrder.paymentTerms || 'cod',
                    notes: selectedOrder.notes || ''
                  });
                  setShowEditDialog(true);
                }}
                className="bg-orange-600 hover:bg-orange-700"
              >
                <Edit size={16} className="mr-2" />
                Edit Order
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Order Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="text-orange-600" />
              Edit Order - {editingOrder?.orderNumber || `SJN-${editingOrder?.id?.slice(0, 8)}`}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-orange-50 p-3 rounded-lg text-sm text-orange-700">
              <strong>Note:</strong> You can modify the order details before it gets approved.
            </div>
            
            <div>
              <Label>Supplier Name</Label>
              <Input
                value={orderForm.supplier}
                onChange={(e) => setOrderForm({...orderForm, supplier: e.target.value})}
                placeholder="e.g., Sunjin Philippines, ABC Feeds"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Order Date</Label>
                <Input
                  type="date"
                  value={orderForm.orderDate}
                  onChange={(e) => setOrderForm({...orderForm, orderDate: e.target.value})}
                />
              </div>
              <div>
                <Label>Expected Delivery</Label>
                <Input
                  type="date"
                  value={orderForm.expectedDelivery}
                  onChange={(e) => setOrderForm({...orderForm, expectedDelivery: e.target.value})}
                />
              </div>
            </div>

            <div>
              <Label>Payment Terms</Label>
              <Select value={orderForm.paymentTerms} onValueChange={(v) => setOrderForm({...orderForm, paymentTerms: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cod">Cash on Delivery</SelectItem>
                  <SelectItem value="credit">Credit (Terms)</SelectItem>
                  <SelectItem value="prepaid">Prepaid</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Current Items */}
            <div className="border rounded-lg p-4">
              <h4 className="font-semibold mb-3">Order Items ({orderForm.items.length})</h4>
              {orderForm.items.length === 0 ? (
                <p className="text-slate-500 text-sm">No items added yet</p>
              ) : (
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {orderForm.items.map((item, idx) => (
                    <div key={idx} className="flex justify-between items-center p-2 bg-slate-50 rounded">
                      <div>
                        <span className="font-medium">{item.productName}</span>
                        <span className="text-slate-500 ml-2">{item.quantity} {item.unit} @ {formatCurrency(item.unitPrice)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold">{formatCurrency(item.totalPrice || (item.quantity * item.unitPrice))}</span>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => {
                            setOrderForm({
                              ...orderForm,
                              items: orderForm.items.filter((_, i) => i !== idx)
                            });
                          }}
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {orderForm.items.length > 0 && (
                <div className="mt-3 pt-3 border-t flex justify-between font-bold">
                  <span>Total</span>
                  <span className="text-orange-600">
                    {formatCurrency(orderForm.items.reduce((sum, item) => sum + (item.totalPrice || item.quantity * item.unitPrice), 0))}
                  </span>
                </div>
              )}
            </div>

            {/* Add New Item */}
            <div className="border rounded-lg p-4">
              <h4 className="font-semibold mb-3">Add Item</h4>
              <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
                <div className="col-span-2 relative">
                  <Input
                    placeholder="Search product name..."
                    value={productSearch}
                    onChange={(e) => {
                      setProductSearch(e.target.value);
                      setShowProductDropdown(true);
                    }}
                    onFocus={() => setShowProductDropdown(true)}
                  />
                  {showProductDropdown && (
                    <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {products
                        .filter(p => 
                          !productSearch || 
                          p.name?.toLowerCase().includes(productSearch.toLowerCase())
                        )
                        .map(p => {
                          const price = p.purchasePrice || p.purchasePricePerSack || p.wholesalePrice || p.price || 0;
                          return (
                            <div
                              key={p.id}
                              className="px-3 py-2 hover:bg-orange-50 cursor-pointer border-b last:border-b-0"
                              onClick={() => {
                                setNewItem({
                                  ...newItem,
                                  productId: p.id,
                                  productName: p.name,
                                  unitPrice: price || ''
                                });
                                setProductSearch(p.name);
                                setShowProductDropdown(false);
                              }}
                            >
                              <div className="flex justify-between items-center">
                                <div className="font-medium text-sm">{p.name}</div>
                                {price > 0 && (
                                  <div className="text-sm font-semibold text-orange-600">₱{price.toLocaleString()}</div>
                                )}
                              </div>
                            </div>
                          );
                        })
                      }
                    </div>
                  )}
                </div>
                <Input
                  type="number"
                  placeholder="Qty"
                  value={newItem.quantity}
                  onChange={(e) => setNewItem({...newItem, quantity: e.target.value})}
                  onFocus={() => setShowProductDropdown(false)}
                />
                <Select value={newItem.unit} onValueChange={(v) => setNewItem({...newItem, unit: v})}>
                  <SelectTrigger onClick={() => setShowProductDropdown(false)}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sack">Sack</SelectItem>
                    <SelectItem value="kg">KG</SelectItem>
                    <SelectItem value="pcs">Pcs</SelectItem>
                    <SelectItem value="box">Box</SelectItem>
                    <SelectItem value="case">Case</SelectItem>
                  </SelectContent>
                </Select>
                <Input
                  type="number"
                  placeholder="Price"
                  value={newItem.unitPrice}
                  onChange={(e) => setNewItem({...newItem, unitPrice: e.target.value})}
                  onFocus={() => setShowProductDropdown(false)}
                />
              </div>
              <Button 
                onClick={() => {
                  if (!newItem.productName || !newItem.quantity) {
                    toast.error('Please fill in product and quantity');
                    return;
                  }
                  setOrderForm({
                    ...orderForm,
                    items: [...orderForm.items, {
                      ...newItem,
                      quantity: parseFloat(newItem.quantity),
                      unitPrice: parseFloat(newItem.unitPrice) || 0,
                      totalPrice: parseFloat(newItem.quantity) * (parseFloat(newItem.unitPrice) || 0)
                    }]
                  });
                  setNewItem({ productId: '', productName: '', quantity: '', unit: 'sack', unitPrice: '' });
                  setProductSearch('');
                }}
                className="mt-2 bg-orange-600 hover:bg-orange-700" 
                size="sm"
              >
                <Plus size={14} className="mr-1" /> Add
              </Button>
            </div>

            <div>
              <Label>Notes</Label>
              <Textarea
                value={orderForm.notes}
                onChange={(e) => setOrderForm({...orderForm, notes: e.target.value})}
                placeholder="Additional notes..."
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowEditDialog(false); setEditingOrder(null); }}>Cancel</Button>
            <Button 
              onClick={async () => {
                try {
                  const payload = {
                    ...orderForm,
                    totalAmount: orderForm.items.reduce((sum, item) => sum + (item.totalPrice || item.quantity * item.unitPrice), 0)
                  };
                  await axios.put(`${API}/api/nas/sunjin/orders/${editingOrder.id}`, payload);
                  toast.success('Order updated successfully!');
                  setShowEditDialog(false);
                  setEditingOrder(null);
                  resetOrderForm();
                  loadOrders();
                } catch (error) {
                  toast.error(error.response?.data?.detail || 'Failed to update order');
                }
              }}
              className="bg-orange-600 hover:bg-orange-700"
            >
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SunjinShopOrders;
